/**
 * FieldToolsSettingsPage — super-admin-only page to configure the
 * Google Maps API keys used by CAP.
 *
 *   • google_geocode_api_key — Geocoding API, powers "Find customers near me"
 *   • google_address_api_key — Places (New) + Address Validation, powers
 *     the AddressAutocomplete component (checkout ship-to, customer edit,
 *     warranty, commissioning)
 *
 * Keys live in Mongo ``field_tools_settings``. They may belong to
 * different Google Cloud projects, so we never cross-reuse them.
 */
import { useEffect, useState } from "react";
import { Key, Eye, EyeSlash, FloppyDisk, ArrowsClockwise, CheckCircle, MapPin, Trash, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminPageHeader from "@/components/AdminPageHeader";


function KeyCard({
  title, description, testidPrefix,
  present, masked, updatedBy, loading,
  onSave, onClear, clearConfirm, apiSetupLink,
}) {
  const [value, setValue] = useState("");
  const [reveal, setReveal] = useState(false);
  const [saving, setSaving] = useState(false);

  const doSave = async () => {
    if (!value.trim()) return;
    setSaving(true);
    try { await onSave(value.trim()); setValue(""); }
    finally { setSaving(false); }
  };
  const doClear = async () => {
    if (!window.confirm(clearConfirm)) return;
    setSaving(true);
    try { await onClear(); } finally { setSaving(false); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5 space-y-3">
      <div className="flex items-center gap-2">
        <Key size={16} weight="bold" className="text-emerald-600" />
        <h2 className="text-sm font-bold tracking-tight">{title}</h2>
      </div>
      <div className="text-[12px] text-slate-600">{description}</div>
      {loading ? (
        <div className="py-6 text-center"><ArrowsClockwise size={20} weight="bold" className="mx-auto animate-spin text-slate-400" /></div>
      ) : (
        <>
          <div className="text-[12px] text-slate-600">
            Current: {present ? (
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <CheckCircle size={11} weight="fill" /> configured
                <span className="font-mono text-slate-700" data-testid={`${testidPrefix}-masked`}>{masked}</span>
                {updatedBy && <span className="text-slate-400 text-[11px]">· last set by {updatedBy}</span>}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-amber-700">
                <Warning size={11} weight="fill" /> not configured
              </span>
            )}
          </div>
          <label className="block">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
              Paste a new API key
            </span>
            <div className="flex gap-1">
              <input type={reveal ? "text" : "password"}
                     value={value}
                     onChange={(e) => setValue(e.target.value)}
                     placeholder="AIzaSy..."
                     data-testid={`${testidPrefix}-key-input`}
                     className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none font-mono" />
              <button type="button" onClick={() => setReveal((v) => !v)}
                      title={reveal ? "Hide" : "Reveal"}
                      data-testid={`${testidPrefix}-toggle-reveal`}
                      className="px-2 border border-slate-300 rounded-sm hover:bg-slate-100 text-slate-600">
                {reveal ? <EyeSlash size={14} /> : <Eye size={14} />}
              </button>
            </div>
            {apiSetupLink}
          </label>
          <div className="flex flex-wrap gap-2 pt-2">
            <button onClick={doSave}
                    disabled={saving || !value.trim()}
                    data-testid={`${testidPrefix}-save`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-50">
              <FloppyDisk size={12} weight="fill" /> Save key
            </button>
            {present && (
              <button onClick={doClear}
                      disabled={saving}
                      data-testid={`${testidPrefix}-clear`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border border-rose-300 text-rose-700 hover:bg-rose-50">
                <Trash size={12} weight="fill" /> Clear current key
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
}


export default function FieldToolsSettingsPage({ user, onLogout, onLogoutAll }) {
  const [loading, setLoading] = useState(true);
  const [geo, setGeo] = useState({ present: false, masked: "", updated_by: "" });
  const [addr, setAddr] = useState({ present: false, masked: "", updated_by: "" });

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/field-tools/settings");
      setGeo({
        present:    !!r.data.google_geocode_api_key_present,
        masked:     r.data.google_geocode_api_key_masked || "",
        updated_by: r.data.updated_by || "",
      });
      setAddr({
        present:    !!r.data.google_address_api_key_present,
        masked:     r.data.google_address_api_key_masked || "",
        updated_by: r.data.address_api_updated_by || "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load settings");
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  const saveKey = (field, humanName) => async (val) => {
    try {
      await api.put("/admin/field-tools/settings", { [field]: val });
      toast.success(`${humanName} saved.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); throw e; }
  };
  const clearKey = (field, humanName) => async () => {
    try {
      await api.put("/admin/field-tools/settings", { [field]: "" });
      toast.success(`${humanName} cleared.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Clear failed"); throw e; }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-['Lato'] text-slate-900" data-testid="field-tools-settings-page">
      <AdminPageHeader sticky maxWidth="max-w-3xl" testId="field-tools-settings-back"
        icon={<MapPin size={20} weight="fill" className="text-emerald-600" />}
        title="Field tools · Settings"
        subtitle="Configure the Google Maps keys that power nearby-customer lookup and address autocomplete."
        user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />

      <main className="max-w-3xl mx-auto p-6 pb-32 space-y-5">
        <section className="bg-blue-50 border border-blue-200 rounded-md p-3 text-[12px] text-blue-900">
          These two keys may belong to different Google Cloud projects. Configure each one independently.
        </section>

        <KeyCard
          title="Google Maps Geocoding API key"
          description="Powers 'Find customers near me'. Enable the Geocoding API on this key."
          testidPrefix="field-tools-settings"
          present={geo.present} masked={geo.masked} updatedBy={geo.updated_by} loading={loading}
          onSave={saveKey("google_geocode_api_key", "Geocoding key")}
          onClear={clearKey("google_geocode_api_key", "Geocoding key")}
          clearConfirm="Clear the Google Geocoding API key? 'Find customers near me' will stop working until a new key is provided."
          apiSetupLink={
            <p className="text-[11px] text-slate-500 mt-1">
              Enable <em>Geocoding API</em> on this key at{" "}
              <a href="https://console.cloud.google.com/google/maps-apis/credentials"
                 target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
                console.cloud.google.com
              </a>.
            </p>
          }
        />

        <KeyCard
          title="Google Places + Address Validation API key"
          description="Powers the address autocomplete + Verify button at checkout, customer edit, warranty, and commissioning."
          testidPrefix="field-tools-settings-address"
          present={addr.present} masked={addr.masked} updatedBy={addr.updated_by} loading={loading}
          onSave={saveKey("google_address_api_key", "Address Services key")}
          onClear={clearKey("google_address_api_key", "Address Services key")}
          clearConfirm="Clear the Google Places + Address Validation key? Address autocomplete and Verify buttons will stop working until a new key is provided."
          apiSetupLink={
            <p className="text-[11px] text-slate-500 mt-1">
              Enable <em>Places API (New)</em> AND <em>Address Validation API</em> on this key at{" "}
              <a href="https://console.cloud.google.com/google/maps-apis/credentials"
                 target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
                console.cloud.google.com
              </a>. May belong to a different Google Cloud project than the geocoding key.
            </p>
          }
        />

        <section className="bg-white border border-slate-200 rounded-md p-5 space-y-2 text-[12px] text-slate-700">
          <h2 className="text-sm font-bold tracking-tight">Who can use the field-tools features?</h2>
          <ul className="list-disc list-inside space-y-1">
            <li>Anyone with an email at a main-company staff domain (see "Staff email domains" in the Admin panel).</li>
            <li>Customer-role users whose company <code className="bg-slate-100 px-1">Customer.Type</code> is
              <em> (A) Distributor</em>, <em>ReSeller</em>, <em>Sub Dealer</em>, or <em>OEM</em>.</li>
            <li>End-user customers do NOT see the record or nearby buttons.</li>
          </ul>
        </section>
      </main>
    </div>
  );
}
